import React, {
    FocusEventHandler,
    KeyboardEventHandler,
    forwardRef,
    useRef,
    useState
} from 'react';
import composeRefs from '@seznam/compose-react-refs';
import {
    TextArea,
    DSFieldContainer,
    DSScrollWrapper,
    HBTagsRow,
    DSInnerTextArea
} from './HBTextArea.styles';
import { HBTextAreaProps } from './HBTextArea.types';
import { stopEventPropagation } from '../../utils/shared.utils';
import { MustIncludeOneOfTestIdProps } from '../common/internal/test-id.types';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import TagsInputRow, { TagsInputRowRef } from '../HBTextField/TagsInputRow';

const HBTextArea = forwardRef<
    HTMLTextAreaElement,
    HBTextAreaProps & MustIncludeOneOfTestIdProps
>((props, ref) => {
    const {
        className,
        size: sizeProp,
        customTheme,
        isPDFPrinting,
        testIdName,
        testId,
        isNewHBMode,
        resizable,
        error,
        onFocus,
        onBlur,
        tags,
        onTagRemove,
        height,
        width,
        ...baseProps
    } = props;

    const size = sizeProp ?? (isNewHBMode ? 'sm' : 'md');

    const { id, name } = baseProps;

    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName || id || name,
        suffix: 'text-area',
        componentName: 'HBTextArea'
    });

    const innerRef = useRef<HTMLTextAreaElement>(null);
    const tagsRowRef = useRef<TagsInputRowRef>(null);
    const [isFocused, setIsFocused] = useState(false);

    const hasTags = (tags?.length ?? 0) > 0;

    const handleFocus: FocusEventHandler<HTMLTextAreaElement> = ev => {
        setIsFocused(true);
        onFocus?.(ev);
    };

    const handleBlur: FocusEventHandler<HTMLTextAreaElement> = ev => {
        setIsFocused(false);
        onBlur?.(ev);
    };

    const handleKeyDownDS: KeyboardEventHandler<HTMLTextAreaElement> = ev => {
        // Backspace on empty textarea removes last tag
        if (
            ev.key === 'Backspace' &&
            !ev.currentTarget.value &&
            tags &&
            tags.length > 0 &&
            typeof onTagRemove === 'function'
        ) {
            onTagRemove(tags[tags.length - 1].id);
        }
        // ArrowLeft at start-of-textarea navigates into the tags row
        if (
            ev.key === 'ArrowLeft' &&
            ev.currentTarget.selectionStart === 0 &&
            ev.currentTarget.selectionEnd === 0 &&
            hasTags
        ) {
            ev.preventDefault();
            tagsRowRef.current?.focusLast();
        }
        baseProps.onKeyDown?.(ev);
    };

    if (isNewHBMode) {
        return (
            <DSFieldContainer
                $size={size}
                $focused={isFocused}
                $error={!!error}
                $disabled={!!baseProps.disabled}
                $resizable={!!resizable}
                $customTheme={customTheme}
                onClick={() => innerRef.current?.focus()}
                data-testid={`${finalTestId}--container`}
            >
                <DSScrollWrapper>
                    {hasTags && tags && (
                        <HBTagsRow>
                            <TagsInputRow
                                ref={tagsRowRef}
                                tags={tags}
                                onTagRemove={onTagRemove}
                                onNavigateToInput={() =>
                                    innerRef.current?.focus()
                                }
                                testIdPrefix={finalTestId}
                            />
                        </HBTagsRow>
                    )}
                    <DSInnerTextArea
                        autoComplete="off"
                        {...baseProps}
                        ref={composeRefs(ref, innerRef)}
                        $size={size}
                        $customTheme={customTheme}
                        data-testid={finalTestId}
                        onKeyDown={handleKeyDownDS}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        onPaste={baseProps?.onPaste || stopEventPropagation}
                    />
                </DSScrollWrapper>
            </DSFieldContainer>
        );
    }

    return (
        // @ts-ignore
        <TextArea
            className={className}
            {...baseProps}
            // @ts-ignore
            as={isPDFPrinting ? 'div' : undefined}
            onPaste={baseProps?.onPaste || stopEventPropagation}
            onFocus={onFocus}
            onBlur={onBlur}
            size={size}
            error={error}
            height={height}
            width={width}
            data-testid={finalTestId}
            customTheme={customTheme}
            ref={ref}
        >
            {(isPDFPrinting && baseProps?.value) || ''}
        </TextArea>
    );
});

HBTextArea.displayName = 'HBTextArea';

export default HBTextArea;
